import cn from "classnames";
import Keyboard, {KeyboardReactInterface} from "react-simple-keyboard";
import "react-simple-keyboard/build/css/index.css";
import React, {
    MutableRefObject,
    RefObject,
    useCallback,
    useEffect,
    useImperativeHandle,
    useMemo,
    useRef,
    useState,
} from "react";
import styles from "./StyledKeyboard.module.css";

const CLOSE_BUTTON_LAYOUT = ["{close}"];
const CLOSE_BUTTON_THEME = {class: "hg-button-close", buttons: "close"};

const EXTENDED_LAYOUT = [
    "` 1 2 3 4 5 6 7 8 9 0 - = {bksp}",
    "{tab} q w e r t y u i o p [ ] \\",
    "{lock} a s d f g h j k l ; ' {enter}",
    "{shift} z x c v b n m , . / {shift}",
    ".com @ {space}",
];

const DEFAULT_LAYOUT = [
    // "1 2 3 4 5 6 7 8 9 0",
    "q w e r t y u i o p",
    "a s d f g h j k l",
    "{shift} z x c v b n m {bksp}",
    "{numbers} {space} {enter}",
];

const EXTENDED_SHIFT_LAYOUT = [
    "~ ! @ # $ % ^ & * ( ) _ + {bksp}",
    "{tab} Q W E R T Y U I O P { } |",
    '{lock} A S D F G H J K L : " {enter}',
    "{shift} Z X C V B N M < > ? {shift}",
    ".com @ {space}",
];

const SHIFT_LAYOUT = [
    // "1 2 3 4 5 6 7 8 9 0",
    "Q W E R T Y U I O P",
    "A S D F G H J K L",
    "{shift} Z X C V B N M {bksp}",
    "{numbers} {space} {enter}",
];

const NUMERIC_WITH_ALPHA_SWITCH = [
    "1 2 3 {bksp}",
    "4 5 6 {enter}",
    "7 8 9 ",
    "{abc} 0 . ",
];

const NUMERIC_LAYOUT = ["1 2 3 {bksp}", "4 5 6 {enter}", "7 8 9 ", " 0  "];

const NUMERIC_WITH_DECIMAL_LAYOUT = [
    "1 2 3 {bksp}",
    "4 5 6 {enter}",
    "7 8 9 ",
    " 0 . ",
];

const NUMERIC_NO_ENTER_LAYOUT = ["1 2 3 {bksp}", "4 5 6 ", "7 8 9 ", " 0  "];

const NUMERIC_KEYBOARD_BUTTONS = "1 2 3 4 5 6 7 8 9 0 . {enter} {bksp} {abc} ";
const DEFAULT_KEYBOARD_BUTTONS =
    "q Q w W e E r R t T y Y u U i I o O p P a A s S d D f F g G h H j J k K l L z Z x X c C v V b B n N m M {shift} {bksp} {numbers} {space} {enter}";

function getKeyboardButtonThemeProp(
    layout: KeyboardLayout,
    disableActiveStyle: boolean,
) {
    if (
        layout === "numeric" ||
        layout === "numeric-decimal" ||
        layout === "numeric-no-enter" ||
        layout === "numeric-alpha-switch"
    ) {
        return [
            CLOSE_BUTTON_THEME,
            ...(disableActiveStyle
                ? [
                      {
                          class: "no-active-style",
                          buttons: NUMERIC_KEYBOARD_BUTTONS,
                      },
                  ]
                : []),
            {
                class: "hg-numeric",
                buttons: NUMERIC_KEYBOARD_BUTTONS, // extra space at the end to also apply this style for the empty button
            },
        ];
    }

    return [
        CLOSE_BUTTON_THEME,
        ...(disableActiveStyle
            ? [
                  {
                      class: "no-active-style",
                      buttons: DEFAULT_KEYBOARD_BUTTONS,
                  },
              ]
            : []),
    ];
}

function getKeyboardLayoutProp(showCloseButton: boolean) {
    return {
        default: showCloseButton
            ? [...CLOSE_BUTTON_LAYOUT, ...DEFAULT_LAYOUT]
            : DEFAULT_LAYOUT,
        extended: showCloseButton
            ? [...CLOSE_BUTTON_LAYOUT, ...EXTENDED_LAYOUT]
            : EXTENDED_LAYOUT,
        shift: showCloseButton
            ? [...CLOSE_BUTTON_LAYOUT, ...SHIFT_LAYOUT]
            : SHIFT_LAYOUT,
        "extended-shift": showCloseButton
            ? [...CLOSE_BUTTON_LAYOUT, ...EXTENDED_SHIFT_LAYOUT]
            : EXTENDED_SHIFT_LAYOUT,
        "numeric-alpha-switch": showCloseButton
            ? [...CLOSE_BUTTON_LAYOUT, ...NUMERIC_WITH_ALPHA_SWITCH]
            : NUMERIC_WITH_ALPHA_SWITCH,
        numeric: showCloseButton
            ? [...CLOSE_BUTTON_LAYOUT, ...NUMERIC_LAYOUT]
            : NUMERIC_LAYOUT,
        "numeric-decimal": showCloseButton
            ? [...CLOSE_BUTTON_LAYOUT, ...NUMERIC_WITH_DECIMAL_LAYOUT]
            : NUMERIC_WITH_DECIMAL_LAYOUT,
        "numeric-no-enter": showCloseButton
            ? [...CLOSE_BUTTON_LAYOUT, ...NUMERIC_NO_ENTER_LAYOUT]
            : NUMERIC_NO_ENTER_LAYOUT,
    };
}

function getKeyboardDisplayProp(enterLabelOverride?: string) {
    return {
        "{bksp}": "\u232b",
        "{enter}": enterLabelOverride ?? "\u23ce",
        "@": "\u0040",
        "{tab}": "\u21e5",
        "{lock}": "\u21ea",
        "{space}": "\u2423",
        "{shift}": "shift",
        "{close}": "Close",
        "{numbers}": "123",
        "{abc}": "ABC",
    };
}

export type StyledKeyboardRefHandler = {
    resetLayout: () => void;
};

type KeyboardLayout =
    | "numeric"
    | "default"
    | "extended"
    | "extended-shift"
    | "shift"
    | "numeric-alpha-switch"
    | "numeric-decimal"
    | "numeric-no-enter";

type Props = {
    layout?: KeyboardLayout;
    currentInput: string;
    onChange: (input: string) => void;
    onPressEnter?: () => void;
    inputRefs: (RefObject<HTMLInputElement> | undefined)[];
    ignoreKeyboardDismissRefs?: (
        | RefObject<HTMLDivElement | HTMLButtonElement>
        | undefined
    )[]; // non input refs that when clicked on, shouldn't dismiss the keyboard
    enterLabel?: string;
    maxLength?: number;
    className?: string;
    disableCaretPositioning?: boolean;
    disableActiveStyle?: boolean;
} & (
    | {
          alwaysVisible: true;
          visible?: never;
          setVisible?: never;
          showCloseButton?: never;
      }
    | {
          alwaysVisible?: never;
          visible: boolean;
          setVisible: (newVal: boolean) => void;
          showCloseButton?: boolean;
      }
);

function StyledKeyboard(
    props: Props,
    ref: React.ForwardedRef<StyledKeyboardRefHandler>,
) {
    const [layout, setLayout] = useState(props.layout ?? "default");
    useImperativeHandle(ref, () => ({
        resetLayout() {
            setLayout(props.layout ?? "default");
        },
    }));

    const keyboardContainer = useRef<HTMLDivElement>(null);
    const keyboard: MutableRefObject<null | KeyboardReactInterface> =
        useRef(null);

    const handleClickOutsideInput = useCallback(
        (event: Event) => {
            if (
                !keyboardContainer.current ||
                (!props.inputRefs.length &&
                    !props.ignoreKeyboardDismissRefs?.length)
            ) {
                return;
            }
            const path = event.composedPath && event.composedPath();

            let clickedOnInput = false;
            props.inputRefs.forEach((inputRef) => {
                if (inputRef) {
                    clickedOnInput =
                        clickedOnInput ||
                        (!!inputRef.current && path.includes(inputRef.current));
                }
            });

            let clickedOnIgnoreDismissalDiv = false;
            props.ignoreKeyboardDismissRefs?.forEach((ref) => {
                if (ref) {
                    clickedOnIgnoreDismissalDiv =
                        clickedOnIgnoreDismissalDiv ||
                        (!!ref.current && path.includes(ref.current));
                }
            });

            if (
                !path.includes(keyboardContainer.current) &&
                !clickedOnInput &&
                !clickedOnIgnoreDismissalDiv
            ) {
                keyboardContainer.current.className = styles.keyboardCollapsed;
                setTimeout(() => {
                    props.setVisible?.(false);
                    setLayout(props.layout ?? "default");
                }, 270);
            }
        },
        [props.inputRefs, props.ignoreKeyboardDismissRefs],
    );

    useEffect(() => {
        if (props.currentInput !== keyboard.current?.getInput()) {
            keyboard.current?.setInput(props.currentInput);
        }
    }, [props.currentInput]);

    useEffect(() => {
        if (props.layout) {
            setLayout(props.layout);
        }
    }, [props.layout]);

    useEffect(() => {
        if (!props.alwaysVisible && props.visible) {
            document.addEventListener("click", handleClickOutsideInput);
        }
        return () => {
            document.removeEventListener("click", handleClickOutsideInput);
        };
    }, [handleClickOutsideInput, props.visible, props.alwaysVisible]);

    const onKeyPress = (button: string) => {
        if (button === "{shift}" || button === "{lock}") {
            handleShift();
        }

        if (button === "{enter}") {
            props.onPressEnter?.();
        }

        if (button === "{numbers}") {
            handleSwitchNumeric();
        }

        if (button === "{abc}") {
            handleSwitchAlpha();
        }

        if (button === "{close}" && keyboardContainer.current) {
            keyboardContainer.current.className = styles.keyboardCollapsed;
            setTimeout(() => {
                props.setVisible?.(false);
                setLayout(props.layout ?? "default");
            }, 270);
        }
    };

    const handleShift = () => {
        setLayout((prevLayout) => {
            if (prevLayout === "extended" || prevLayout === "extended-shift") {
                return prevLayout === "extended"
                    ? "extended-shift"
                    : "extended";
            }
            return prevLayout === "default" ? "shift" : "default";
        });
    };

    const handleSwitchNumeric = () => {
        setLayout("numeric-alpha-switch");
    };

    const handleSwitchAlpha = () => {
        setLayout("default");
    };

    const keyboardButtonThemeProp = useMemo(
        () =>
            getKeyboardButtonThemeProp(
                layout,
                props.disableActiveStyle ?? false,
            ),
        [layout, props.disableActiveStyle],
    );

    const keyboardDisplayProp = useMemo(
        () => getKeyboardDisplayProp(props.enterLabel),
        [props.enterLabel],
    );

    const keyboardLayoutProp = useMemo(
        () => getKeyboardLayoutProp(props.showCloseButton ?? false),
        [props.showCloseButton],
    );

    if (!props.alwaysVisible && !props.visible) {
        return null;
    }

    return (
        <div
            ref={keyboardContainer}
            className={cn(styles.keyboard, props.className)}>
            <Keyboard
                keyboardRef={(r: KeyboardReactInterface) => {
                    r.setInput(props.currentInput);
                    keyboard.current = r;
                }}
                layoutName={layout}
                layout={keyboardLayoutProp}
                buttonTheme={keyboardButtonThemeProp}
                display={keyboardDisplayProp}
                onChange={props.onChange}
                onKeyPress={onKeyPress}
                maxLength={props.maxLength}
                disableCaretPositioning={props.disableCaretPositioning}
            />
        </div>
    );
}

export default React.forwardRef<StyledKeyboardRefHandler, Props>(
    StyledKeyboard,
);
